import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, 
  Search,
  RefreshCw,
  User,
  LogIn,
  LogOut,
  FileText,
  Trash2,
  Edit,
  CreditCard,
  Mail,
  Shield,
  Key,
  Filter
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

const ACTION_ICONS = {
  login: LogIn,
  logout: LogOut,
  login_failed: LogIn,
  quote_created: FileText,
  quote_updated: Edit,
  quote_deleted: Trash2,
  quote_status_changed: Edit,
  aci_created: FileText,
  aci_deleted: Trash2,
  aci_payment_synced: CreditCard,
  aci_email_resent: Mail,
  price_updated: Edit,
  user_created: User,
  user_updated: Edit,
  user_deleted: Trash2,
  user_password_reset: Key,
  "2fa_enabled": Shield,
  "2fa_disabled": Shield,
  backup_codes_generated: Key,
  backup_code_used: Key
};

const ACTION_COLORS = {
  login: "text-green-500",
  logout: "text-slate-400",
  login_failed: "text-red-500",
  quote_created: "text-blue-500",
  quote_updated: "text-amber-500",
  quote_deleted: "text-red-500",
  quote_status_changed: "text-amber-500",
  aci_created: "text-blue-500",
  aci_deleted: "text-red-500",
  aci_payment_synced: "text-green-500",
  aci_email_resent: "text-blue-500",
  price_updated: "text-amber-500",
  user_created: "text-blue-500",
  user_updated: "text-amber-500",
  user_deleted: "text-red-500",
  user_password_reset: "text-amber-500",
  "2fa_enabled": "text-green-500",
  "2fa_disabled": "text-red-500",
  backup_codes_generated: "text-blue-500",
  backup_code_used: "text-amber-500"
};

const ACTION_LABELS = {
  login: "Login",
  logout: "Logout",
  login_failed: "Login Failed",
  quote_created: "Quote Created",
  quote_updated: "Quote Updated",
  quote_deleted: "Quote Deleted",
  quote_status_changed: "Status Changed",
  aci_created: "ACI Created",
  aci_deleted: "ACI Deleted",
  aci_payment_synced: "Payment Synced",
  aci_email_resent: "Email Resent",
  price_updated: "Price Updated",
  user_created: "User Created",
  user_updated: "User Updated",
  user_deleted: "User Deleted",
  user_password_reset: "Password Reset",
  "2fa_enabled": "2FA Enabled",
  "2fa_disabled": "2FA Disabled",
  backup_codes_generated: "Backup Codes Generated",
  backup_code_used: "Backup Code Used"
};

export default function ActivityLogsPage() {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [selectedAction, setSelectedAction] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 50;

  // Only OWNER can access
  useEffect(() => {
    if (admin && admin.role !== "owner" && admin.role !== "admin") {
      navigate("/admin");
      toast.error("Acces restricționat la Activity Logs");
    }
  }, [admin, navigate]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("limit", limit);
      params.append("offset", page * limit);
      if (selectedAction) params.append("action", selectedAction);
      if (selectedUser) params.append("admin_username", selectedUser);

      const response = await api.get(`/admin/activity-logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      toast.error("Eroare la încărcarea logurilor");
    } finally {
      setLoading(false);
    }
  };

  const fetchActions = async () => {
    try {
      const response = await api.get("/admin/activity-logs/actions");
      setActions(response.data.actions || []);
    } catch (error) {
      console.error("Failed to fetch actions", error);
    }
  };

  const fetchCount = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedAction) params.append("action", selectedAction);
      if (selectedUser) params.append("admin_username", selectedUser);
      
      const response = await api.get(`/admin/activity-logs/count?${params.toString()}`);
      setTotalCount(response.data.count || 0);
    } catch (error) {
      console.error("Failed to fetch count", error);
    }
  };

  useEffect(() => {
    fetchActions();
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchCount();
  }, [page, selectedAction, selectedUser]);

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getActionIcon = (action) => {
    const Icon = ACTION_ICONS[action] || FileText;
    return Icon;
  };

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="bg-[#0f172a] border-b border-slate-800 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/admin")}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Înapoi
            </Button>
            <h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-white">
              Activity Logs
            </h1>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setPage(0); fetchLogs(); }}
            className="border-slate-700 text-slate-400 hover:text-white"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="bg-[#0f172a] border border-slate-800 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-slate-400" />
            <span className="text-slate-400 text-sm font-medium">Filtre</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Acțiune</label>
              <select
                value={selectedAction}
                onChange={(e) => { setSelectedAction(e.target.value); setPage(0); }}
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm"
              >
                <option value="">Toate acțiunile</option>
                {actions.map((action) => (
                  <option key={action} value={action}>
                    {ACTION_LABELS[action] || action}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Utilizator</label>
              <input
                type="text"
                value={selectedUser}
                onChange={(e) => { setSelectedUser(e.target.value); setPage(0); }}
                placeholder="Username..."
                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm"
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setSelectedAction(""); setSelectedUser(""); setPage(0); }}
                className="border-slate-700 text-slate-400 hover:text-white"
              >
                Resetează filtrele
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-4 text-slate-400 text-sm">
          Total: {totalCount} înregistrări
        </div>

        {/* Logs Table */}
        <div className="bg-[#0f172a] border border-slate-800 rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-400">
              Se încarcă...
            </div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              Nu există loguri pentru filtrele selectate
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Data</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Utilizator</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Acțiune</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Detalii</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {logs.map((log) => {
                    const ActionIcon = getActionIcon(log.action);
                    const actionColor = ACTION_COLORS[log.action] || "text-slate-400";
                    return (
                      <tr key={log.id} className="hover:bg-slate-900/30">
                        <td className="px-4 py-3 text-sm text-slate-300 whitespace-nowrap">
                          {formatDate(log.timestamp)}
                        </td>
                        <td className="px-4 py-3 text-sm text-white font-medium">
                          {log.admin_username}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <ActionIcon className={`w-4 h-4 ${actionColor}`} />
                            <span className={`text-sm ${actionColor}`}>
                              {ACTION_LABELS[log.action] || log.action}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-400 max-w-xs truncate">
                          {log.details && Object.keys(log.details).length > 0 ? (
                            <span title={JSON.stringify(log.details, null, 2)}>
                              {Object.entries(log.details).slice(0, 2).map(([k, v]) => (
                                <span key={k} className="mr-2">
                                  {k}: <span className="text-slate-300">{String(v).substring(0, 30)}</span>
                                </span>
                              ))}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500 font-mono">
                          {log.ip_address || "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalCount > limit && (
          <div className="mt-4 flex justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="border-slate-700 text-slate-400"
            >
              Anterior
            </Button>
            <span className="px-4 py-2 text-slate-400 text-sm">
              Pagina {page + 1} din {Math.ceil(totalCount / limit)}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={(page + 1) * limit >= totalCount}
              className="border-slate-700 text-slate-400"
            >
              Următor
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
